/**
 * Consent management — versioned preferences, Google Consent Mode v2,
 * GPC honoring, HubSpot third-party consent (_hsp), and event bus.
 *
 * Categories:
 *   - necessary   (always granted; security, forms, session, saved choice)
 *   - analytics   (page visits, calculator progress, errors)
 *   - advertising (attribution, remarketing) — GPC forces denied
 *   - experience  (saved prefs, optional 3P features)
 *
 * Loading rules (Google "advanced" Consent Mode):
 *   - GA4 (direct tag), Google Ads tag, GTM container, HubSpot tracker load
 *     IMMEDIATELY at startup, before any consent choice, while the denied
 *     Consent Mode v2 defaults (index.html) are in effect. The visitor's
 *     choice upgrades/downgrades storage afterwards via gtag consent update.
 *   - Meta pixel      → stays opt-in: the GTM container loads with fbq
 *                          consent REVOKED, and Meta events fire only after
 *                          Advertising is granted (GPC keeps it revoked).
 *   - HubSpot         → loads with doNotTrack queued until Analytics granted.
 *   - EVERY vendor    → only while the visitor is on a public, trackable
 *                          route (see `src/lib/public-routes.ts`). This is a
 *                          vendor-side condition layered on top of the stored
 *                          choice; the stored choice itself is never rewritten
 *                          by routing, and lead capture is never blocked.
 *
 * GTM container GTM-WGFZFB8R (verified live, version 6) contains exactly three
 * Meta (Facebook) Custom HTML tags and NO GA4 / Google Ads tags. It is loaded
 * on its OWN `window.marketingDataLayer` (`src/lib/marketing-datalayer.ts`) so
 * Google gtag/consent commands on `window.dataLayer` can never trigger a Meta
 * tag. Meta consent (`fbq('consent', …)`) is synchronised BEFORE consent
 * subscribers run, and no denied-period Meta event is ever queued or replayed.
 *
 * GA4 and Google Ads are SEPARATE Google tags (the combined tag was split, and
 * the Ads destination reassigned to "Magic Builders — Google Ads"). They are
 * configured independently through `src/lib/google-tag.ts`, in either consent
 * order, and a denied destination is never initialized. If GTM ever starts
 * firing its own GA4 or Ads tag, remove the direct configuration first — two
 * owners means double counting.
 */

import {
  GA4_MEASUREMENT_ID as GA_MEASUREMENT_ID,
  configureGoogleTagDestination,
  ensureGtag,
} from "./google-tag";
import { applySharedPageContext, safeCampaignFields, safePagePath } from "./page-context";
import { isPublicTrackableRoute } from "./public-routes";
import { loadMarketingContainer, setMarketingConsent } from "./marketing-datalayer";
import { markVendorLoaded } from "./vendor-route-boundary";
import { purgeLeadMemoryStorage } from "./lead-memory-storage";

export type ConsentCategory = "necessary" | "analytics" | "advertising" | "experience";

export interface ConsentState {
  version: number;
  timestamp: string;
  necessary: true;
  analytics: boolean;
  advertising: boolean;
  experience: boolean;
  /** true = user has made an explicit choice (not just defaults) */
  set: boolean;
  /** true = GPC signal detected, advertising is force-denied */
  gpc: boolean;
}

const STORAGE_KEY = "mb-consent-v1";
const LEGACY_KEY = "cookie_consent";
const CCPA_KEY = "mb-ccpa-opt-out";
export const CONSENT_SCHEMA_VERSION = 1;

const HUBSPOT_PORTAL_ID = "45812212";

type Listener = (state: ConsentState) => void;
const listeners = new Set<Listener>();

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _hsq?: any[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _hsp?: any[];
  }
}

const hasGPC = (): boolean => {
  if (typeof navigator === "undefined") return false;
  // @ts-expect-error non-standard prop
  return navigator.globalPrivacyControl === true;
};

const defaultState = (): ConsentState => ({
  version: CONSENT_SCHEMA_VERSION,
  timestamp: new Date().toISOString(),
  necessary: true,
  analytics: false,
  advertising: false,
  experience: false,
  set: false,
  gpc: hasGPC(),
});

/** Migrate legacy `cookie_consent` = "accepted" | "declined" */
const migrateLegacy = (): ConsentState | null => {
  try {
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy !== "accepted" && legacy !== "declined") return null;
    const gpc = hasGPC();
    const accepted = legacy === "accepted";
    const migrated: ConsentState = {
      version: CONSENT_SCHEMA_VERSION,
      timestamp: new Date().toISOString(),
      necessary: true,
      analytics: accepted,
      advertising: accepted && !gpc,
      experience: accepted,
      set: true,
      gpc,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
    localStorage.removeItem(LEGACY_KEY);
    return migrated;
  } catch {
    return null;
  }
};

const readStored = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return migrateLegacy();
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    if (typeof parsed !== "object" || !parsed) return null;
    const gpc = hasGPC();
    const ccpaOptOut = localStorage.getItem(CCPA_KEY) === "true" || gpc;
    return {
      version: CONSENT_SCHEMA_VERSION,
      timestamp: parsed.timestamp ?? new Date().toISOString(),
      necessary: true,
      analytics: Boolean(parsed.analytics),
      advertising: Boolean(parsed.advertising) && !ccpaOptOut,
      experience: Boolean(parsed.experience),
      set: Boolean(parsed.set),
      gpc,
    };
  } catch {
    return null;
  }
};

let current: ConsentState = defaultState();
let initialized = false;

/**
 * In-memory latch for an EXPLICIT "Do Not Sell or Share" (sale/sharing)
 * opt-out. `localStorage` can throw (private mode, full quota, blocked
 * third-party storage), so the persisted marker alone is not a reliable
 * record of the choice. Once latched it holds for the rest of the session and
 * a later generic "Allow optional cookies" can never reverse it.
 */
let explicitSaleOptOut = false;

/** Record an explicit sale/sharing opt-out in memory (survives storage failure). */
export function recordExplicitSaleOptOut(): void {
  explicitSaleOptOut = true;
}

/** True when an explicit sale/sharing opt-out is latched in memory. */
export function hasExplicitSaleOptOut(): boolean {
  return explicitSaleOptOut;
}

/** Test-only reset of the in-memory latch. */
export function __resetSaleOptOutLatchForTests(): void {
  explicitSaleOptOut = false;
}

/**
 * Central lead-memory enforcement. Stored contact details are purged whenever
 * the Experience category is not granted — at consent init and on every change
 * — so removal never depends on a `useLeadMemory` component being mounted.
 */
const enforceLeadMemoryConsent = (s: ConsentState) => {
  if (s.experience !== true) purgeLeadMemoryStorage();
};

const applyGtagConsent = (s: ConsentState) => {
  if (typeof window === "undefined") return;
  const gtag = ensureGtag();
  if (!gtag) return;
  gtag("consent", "update", {
    ad_storage: s.advertising ? "granted" : "denied",
    ad_user_data: s.advertising ? "granted" : "denied",
    ad_personalization: s.advertising ? "granted" : "denied",
    analytics_storage: s.analytics ? "granted" : "denied",
    functionality_storage: s.experience ? "granted" : "denied",
    personalization_storage: s.experience ? "granted" : "denied",
    security_storage: "granted",
  });
  // Documented gtag command (not a bare dataLayer object).
  gtag("set", "ads_data_redaction", !s.advertising);
  // Advanced Consent Mode: analytics_storage "denied" already enforces
  // cookie-less measurement. The ga-disable-* kill switch would suppress ALL
  // GA4 transmission (including anonymized pings), contradicting the banner's
  // promise of cookie-less signals under "Necessary only" — so it is removed.
};

const applyHubSpotConsent = (s: ConsentState) => {
  if (typeof window === "undefined") return;
  try {
    window._hsq = window._hsq || [];
    if (s.analytics) {
      // Re-enable tracking after a previous doNotTrack.
      window._hsq.push(["doNotTrack", { track: true }]);
    } else {
      window._hsq.push(["doNotTrack"]);
    }
    // Third-party consent API — mirrors our categories to HubSpot.
    window._hsp = window._hsp || [];
    window._hsp.push([
      "setHubSpotConsent",
      {
        analytics: s.analytics,
        advertisement: s.advertising,
        functionality: s.experience,
      },
    ]);
  } catch { /* noop */ }
};

/**
 * True when third-party vendors may run for the CURRENT route. A private or
 * unknown route never loads a vendor, even with consent granted.
 */
export function vendorsAllowedHere(): boolean {
  if (typeof window === "undefined") return false;
  return isPublicTrackableRoute(window.location.pathname);
}

/** Meta consent + container, on the isolated marketing data layer. */
const applyMarketingConsent = (s: ConsentState) => {
  const allowed = s.advertising === true && s.gpc !== true && !advertisingSuppressed();
  // Synchronise fbq consent FIRST, so no consent subscriber can emit an event
  // under a stale grant. A revoke that lands while the container script is
  // still downloading is re-asserted on load by the marketing module.
  setMarketingConsent(allowed);
  // Advanced Consent Mode: the container loads immediately (fbq consent stays
  // revoked until Advertising is granted, so Meta remains opt-in in effect).
  if (vendorsAllowedHere()) {
    loadMarketingContainer();
    markVendorLoaded();
  }
};

const loadGA4 = () => {
  // GA4 is now its own tag (the combined tag was split). Explicit destination
  // config; the Ads destination is configured separately and only under
  // advertising consent.
  //
  // send_page_view: false — ConsentAwareAnalytics owns every SPA page_view, and
  // the account-side "page views on browser history change" setting is saved
  // OFF, so there is exactly one owner of the page_view.
  //
  // The shared SAFE page context is set BEFORE the config so Google's own
  // automatic collection (session_start, user_engagement, …) inherits the
  // sanitized values rather than reading the raw document URL.
  const ctx = applySharedPageContext();
  configureGoogleTagDestination(GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
    ...(ctx ?? {}),
    // Standard GA4 acquisition fields, present from the FIRST config so the
    // session is attributed to the campaign rather than to (direct).
    ...safeCampaignFields(),
  });
  markVendorLoaded();
};

/**
 * Path (pathname + search) for which HubSpot's loader auto-fired the initial
 * page view. SPA route tracker uses this to avoid a duplicate initial call.
 */
let hubspotInitialPath: string | null = null;
let hubspotLastTrackedPath: string | null = null;

/**
 * The ONLY path shape any HubSpot call may see: sanitized pathname, no query,
 * no hash, no token segment. HubSpot keeps the contact details a visitor
 * deliberately submitted (with their consent evidence); their browsing URLs
 * are a different matter and are reduced here.
 */
const currentPath = (): string => {
  if (typeof window === "undefined") return "";
  return safePagePath(window.location.pathname);
};

/** Sanitize any caller-supplied path down to the safe public shape. */
const safeHubSpotPath = (path?: string): string | null => {
  const raw = typeof path === "string" && path.startsWith("/") ? path : undefined;
  const p = raw ? safePagePath(raw) : currentPath();
  if (!p) return null;
  if (!isPublicTrackableRoute(p)) return null;
  return p;
};

const loadHubSpot = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("hs-script-loader")) return;
  // Documented HubSpot flag — must be set BEFORE the tracking script loads.
  // Suppresses HubSpot's own cookie banner because ours is the source of truth.
  (window as unknown as { disableHubSpotCookieBanner?: boolean }).disableHubSpotCookieBanner = true;
  // Remember the path HubSpot's loader will self-fire so SPA tracker skips a dup.
  hubspotInitialPath = currentPath();
  const s = document.createElement("script");
  s.id = "hs-script-loader";
  s.async = true;
  s.defer = true;
  s.type = "text/javascript";
  s.src = `//js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
  document.head.appendChild(s);
  markVendorLoaded();
};

/**
 * Fire a HubSpot SPA page view. No-ops when Analytics consent is denied or
 * when called for the path HubSpot's loader already auto-tracked. Safe to
 * call on every React Router path/search change.
 */
export function trackHubSpotPageView(path?: string): void {
  if (typeof window === "undefined") return;
  if (!current.analytics) return;
  const p = safeHubSpotPath(path);
  if (!p) return;
  if (hubspotLastTrackedPath === p) return;
  if (hubspotInitialPath && p === hubspotInitialPath) {
    // Loader will self-fire (or already has) — consume the initial slot once.
    hubspotInitialPath = null;
    hubspotLastTrackedPath = p;
    return;
  }
  try {
    window._hsq = window._hsq || [];
    window._hsq.push(["setPath", p]);
    window._hsq.push(["trackPageView"]);
    hubspotLastTrackedPath = p;
  } catch { /* noop */ }
}

/**
 * Identify a known visitor to HubSpot after a successful FINAL submission.
 * Consent-gated (Analytics required). Never forwards the email to GA / gtag /
 * dataLayer. Callers should invoke this only on non-partial success paths.
 */
export function identifyHubSpotContact(
  email: string,
  traits?: Record<string, string | number | boolean | undefined>,
): void {
  if (typeof window === "undefined") return;
  if (!current.analytics) return;
  if (!email || typeof email !== "string" || !email.includes("@")) return;
  try {
    const cleaned: Record<string, string | number | boolean> = { email };
    if (traits) {
      for (const [k, v] of Object.entries(traits)) {
        if (v === undefined || v === "" || v === null) continue;
        cleaned[k] = v as string | number | boolean;
      }
    }
    window._hsq = window._hsq || [];
    // Flush against the sanitized path too — an identify is followed by a
    // page view, and that page view must not carry a raw or private url.
    const safePath = safeHubSpotPath();
    if (safePath) {
      window._hsq.push(["setPath", safePath]);
      hubspotLastTrackedPath = safePath;
    }
    window._hsq.push(["identify", cleaned]);
    // trackPageView flushes the identity so it associates with the cookie.
    window._hsq.push(["trackPageView"]);
  } catch { /* noop */ }
}


const notify = () => {
  listeners.forEach((l) => {
    try { l(current); } catch { /* noop */ }
  });
};

const persist = () => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(current)); } catch { /* private mode */ }
};

/**
 * True while a curated MARKETING (Meta) event may be emitted right now.
 *
 * `window.dataLayer` is Google-only these days — the Meta container listens on
 * `window.marketingDataLayer` — so this no longer gates Google measurement at
 * all. There is no "not loaded yet, push anyway" allowance: advertising must be
 * currently permitted, with no GPC and no device opt-out, on a public route.
 */
export function dataLayerEventsAllowed(): boolean {
  return (
    current.advertising === true &&
    current.gpc !== true &&
    !advertisingSuppressed() &&
    vendorsAllowedHere()
  );
}

const applyLoaders = (_s: ConsentState) => {
  // No vendor may load on a private or unknown route, whatever the consent is.
  if (!vendorsAllowedHere()) return;
  // Advanced Consent Mode: GA4 and HubSpot load immediately under the denied
  // defaults; the consent update flow upgrades/downgrades storage afterwards
  // (GA4 kill switch + HubSpot doNotTrack still reflect the choice).
  loadGA4();
  loadHubSpot();
};

export function initConsent(): ConsentState {
  if (initialized) return current;
  initialized = true;
  if (typeof window === "undefined") return current;

  ensureGtag();

  const stored = readStored();
  current = stored ?? defaultState();
  if (current.gpc) current.advertising = false;
  // A stored "Do Not Sell or Share" device opt-out outranks any stored grant.
  if (current.advertising && advertisingSuppressed()) current.advertising = false;

  applyGtagConsent(current);
  applyHubSpotConsent(current);
  // Meta consent is applied before anything else can emit an event.
  applyMarketingConsent(current);
  // Experience denied at boot ⇒ any stored contact details are removed now,
  // with no component mounted.
  enforceLeadMemoryConsent(current);

  // Advanced Consent Mode: vendors load at startup even before any choice.
  applyLoaders(current);

  // Withdrawal (or opt-out) in another tab must apply here too.
  window.addEventListener("storage", (e) => {
    if (e.key === null || e.key === STORAGE_KEY || e.key === CCPA_KEY) syncFromStorage();
  });

  notify();
  return current;

}

export function getConsent(): ConsentState { return current; }

/**
 * True once `initConsent()` has run. Before that, `getConsent()` returns the
 * DEFAULT (all-denied) state, which is not the same thing as a visitor having
 * denied anything — callers that would erase data on a denial must wait.
 */
export function isConsentInitialized(): boolean { return initialized; }

export function subscribeConsent(fn: Listener): () => void {
  listeners.add(fn);
  fn(current);
  return () => { listeners.delete(fn); };
}

export interface ConsentChoice {
  analytics: boolean;
  advertising: boolean;
  experience: boolean;
}

/**
 * True when advertising must stay denied regardless of what the visitor picks
 * in the banner: a browser GPC signal, or a stored "Do Not Sell or Share"
 * device opt-out. A generic "Allow optional cookies" must never silently
 * reverse a specific opt-out the visitor already made.
 */
export function advertisingSuppressed(): boolean {
  if (hasGPC()) return true;
  // In-memory explicit opt-out first: it holds even if nothing persisted.
  if (explicitSaleOptOut) return true;
  try {
    const stored = localStorage.getItem(CCPA_KEY);
    // Accept any recorded opt-out marker ("true" today, "1" from earlier builds).
    if (stored === "true" || stored === "1") {
      // Persisted choice re-arms the latch for this session.
      explicitSaleOptOut = true;
      return true;
    }
    return false;
  } catch {
    return false;
  }

}

export function setConsent(choice: ConsentChoice): ConsentState {
  const wasAnalyticsGranted = current.analytics;
  const gpc = hasGPC();
  const suppressed = advertisingSuppressed();
  // In-memory state is authoritative even when persistence fails (private
  // mode / full quota): a denial must hold for the rest of the session.
  current = {
    version: CONSENT_SCHEMA_VERSION,
    timestamp: new Date().toISOString(),
    necessary: true,
    analytics: Boolean(choice.analytics),
    advertising: Boolean(choice.advertising) && !suppressed,
    experience: Boolean(choice.experience),
    set: true,
    gpc,
  };
  persist();
  applyGtagConsent(current);
  applyHubSpotConsent(current);
  applyMarketingConsent(current);
  enforceLeadMemoryConsent(current);
  if (wasAnalyticsGranted && !current.analytics) {
    hubspotLastTrackedPath = null;
  }
  applyLoaders(current);
  notify();
  return current;
}

/**
 * Re-read persisted consent (and the device opt-out) after another tab changed
 * it, so a withdrawal in one tab takes effect everywhere immediately.
 */
function syncFromStorage() {
  const stored = readStored();
  if (!stored) return;
  const next: ConsentState = {
    ...stored,
    advertising: stored.advertising && !advertisingSuppressed(),
  };
  const changed =
    next.analytics !== current.analytics ||
    next.advertising !== current.advertising ||
    next.experience !== current.experience ||
    next.set !== current.set;
  if (!changed) return;
  const wasAnalyticsGranted = current.analytics;
  current = next;
  applyGtagConsent(current);
  applyHubSpotConsent(current);
  applyMarketingConsent(current);
  enforceLeadMemoryConsent(current);
  if (wasAnalyticsGranted && !current.analytics) hubspotLastTrackedPath = null;
  applyLoaders(current);
  notify();
}


export const acceptAll = () =>
  setConsent({ analytics: true, advertising: true, experience: true });

export const rejectAll = () =>
  setConsent({ analytics: false, advertising: false, experience: false });

export function optOutAdvertising(): ConsentState {
  // Latch FIRST: the denial must hold even if persistence failed and even if
  // the visitor later presses "Allow optional cookies".
  recordExplicitSaleOptOut();
  return setConsent({
    analytics: current.analytics,
    advertising: false,
    experience: current.experience,
  });
}

export function openConsentPreferences(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("consent:open-preferences"));
}
